// ============================================================================
// GROUP COVER UPLOAD
// ----------------------------------------------------------------------------
// Group cover photos live in the uploader's own Storage folder
// (profileImages/{uid}/...), the one path players may write to. The server
// only accepts cover URLs under the caller's folder, so a member cannot
// point a group at someone else's file. Images are resized client-side
// before upload so covers stay small.
// ============================================================================
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from './firebase';

const MAX_W = 1400;
const MAX_H = 800;

async function resizeToJpeg(file: File): Promise<Blob> {
  const bitmap = await createImageBitmap(file).catch(() => null);
  if (!bitmap) return file;
  const scale = Math.min(1, MAX_W / bitmap.width, MAX_H / bitmap.height);
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return file;
  ctx.drawImage(bitmap, 0, 0, w, h);
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b || file), 'image/jpeg', 0.86));
}

/** Upload a cover photo for a group; returns the download URL to save on the group. */
export async function uploadGroupCover(uid: string, groupId: string, file: File): Promise<string> {
  if (!file.type.startsWith('image/')) throw new Error('Pick an image file');
  if (file.size > 10 * 1024 * 1024) throw new Error('Image must be under 10 MB');
  const blob = await resizeToJpeg(file);
  const path = `profileImages/${uid}/group-${groupId}-${Date.now()}.jpg`;
  const r = ref(storage, path);
  await uploadBytes(r, blob, { contentType: 'image/jpeg' });
  return getDownloadURL(r);
}
